import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, Label } from 'reactstrap'
import Select from 'react-select'
import { AsyncPaginate } from 'react-select-async-paginate'
import { TagsInput } from 'react-tag-input-component'
import { request } from '../../../Redux/Sagas/requests/api'
import { useDispatch } from 'react-redux'
import { setNotification } from '../../../Redux/Reducers/notificationHandling'
import { primary_color } from '../../../Helper/uiHelper'

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
]

const weeklyDays = [
  { value: 'mon', label: 'Monday' },
  { value: 'tue', label: 'Tuesday' },
  { value: 'wed', label: 'Wednesday' },
  { value: 'thu', label: 'Thursday' },
  { value: 'fri', label: 'Friday' },
  { value: 'sat', label: 'Saturday' },
  { value: 'sun', label: 'Sunday' },
]

const monthlyDays = ['1', '7', '14', '21', '28'].map((d) => ({ value: d, label: `${d}` }))

const selectStyles = {
  menu: (base) => ({ ...base, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
}

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim())

const appendEmail = (emails, raw) => {
  const email = String(raw || '').trim().toLowerCase()
  if (!isValidEmail(email)) return emails
  if (emails.some((existing) => existing.toLowerCase() === email)) return emails
  return [...emails, email]
}

const emptyForm = (defaultName = 'Report schedule') => ({
  name: defaultName,
  frequency: 'weekly',
  frequency_type: 'wed',
  send_time: '07:00',
  active: true,
  selectedSystemUsers: [],
  freeEmails: [],
})

/**
 * Opens a "Manage emails" modal for report email cron schedules (report_key scoped).
 */
const ReportEmailSchedulePanel = ({
  reportKey = 'ytd_revenue',
  buttonLabel = 'Manage emails',
  defaultScheduleName,
}) => {
  const dispatch = useDispatch()
  const [manageOpen, setManageOpen] = useState(false)
  const [formOpen, setFormOpen] = useState(false)
  const [schedules, setSchedules] = useState([])
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState(null)
  const scheduleName =
    defaultScheduleName ||
    (reportKey === 'school_sales_comparison' ? 'Bookings By School' : reportKey === 'ytd_revenue' ? 'YTD Revenue' : 'Report schedule')
  const [form, setForm] = useState(() => emptyForm(scheduleName))
  const emailTagsRef = useRef(null)
  const [userSearchInput, setUserSearchInput] = useState('')

  const typeOptions = form.frequency === 'weekly' ? weeklyDays : form.frequency === 'monthly' ? monthlyDays : []

  const reduceUserOptions = useCallback((prev, loaded) => {
    const seen = new Set(prev.map((option) => option.value))
    const merged = [...prev]
    loaded.forEach((option) => {
      if (!seen.has(option.value)) {
        seen.add(option.value)
        merged.push(option)
      }
    })
    return merged
  }, [])

  const loadUserOptions = useCallback(async (search, _loadedOptions, additional) => {
    const page = additional?.page ?? 1
    const res = await request(
      'report-email-schedules/users',
      'GET',
      {},
      {},
      { q: search || '', page, pageSize: 20 }
    )
    const meta = res?.data?.meta || {}
    const options = (res?.data?.data || []).map((u) => ({
      value: String(u.id),
      label: u.label,
      email: u.email,
    }))
    const hasMore =
      res?.data?.has_more === true ||
      res?.data?.links?.next === true ||
      (meta.current_page && meta.last_page && meta.current_page < meta.last_page)

    return {
      options,
      hasMore: Boolean(hasMore),
      additional: { page: page + 1 },
    }
  }, [])

  const commitPendingEmail = (rawValue) => {
    const input = emailTagsRef.current?.querySelector('input.rti--input')
    const raw = rawValue ?? input?.value ?? ''
    const next = appendEmail(form.freeEmails, raw)
    if (input && rawValue === undefined) {
      input.value = ''
    }
    if (next.length !== form.freeEmails.length) {
      setForm({ ...form, freeEmails: next })
    }
    return next
  }

  const handleEmailBlur = (e) => {
    if (!e.target.value?.trim()) return
    commitPendingEmail(e.target.value)
    e.target.value = ''
  }

  const loadSchedules = () => {
    setLoading(true)
    request('report-email-schedules', 'GET', {}, {}, { report_key: reportKey }).then((res) => {
      setLoading(false)
      if (res?.status === 200) {
        setSchedules(res?.data?.data || [])
      }
    })
  }

  useEffect(() => {
    if (!manageOpen) return
    loadSchedules()
  }, [manageOpen, reportKey])

  const recipientToSystemUser = (recipient) => ({
    value: String(recipient.user_id),
    label: recipient.user
      ? `${[recipient.user.first_name, recipient.user.surname].filter(Boolean).join(' ')} <${recipient.email}>`.trim()
      : recipient.email,
    email: recipient.email,
  })

  const openCreate = () => {
    setEditing(null)
    setUserSearchInput('')
    setForm(emptyForm(scheduleName))
    setFormOpen(true)
  }

  const openEdit = (row) => {
    const selectedSystemUsers = (row.recipients || [])
      .filter((r) => r.user_id)
      .map(recipientToSystemUser)
    const freeEmails = (row.recipients || []).filter((r) => !r.user_id && r.email).map((r) => r.email)
    setEditing(row)
    setUserSearchInput('')
    setForm({
      name: row.name || '',
      frequency: row.frequency || 'weekly',
      frequency_type: row.frequency_type || (row.frequency === 'weekly' ? 'wed' : '1'),
      send_time: (row.send_time || '07:00:00').slice(0, 5),
      active: !!row.active,
      selectedSystemUsers,
      freeEmails,
    })
    setFormOpen(true)
  }

  const buildRecipients = (freeEmails = form.freeEmails) => {
    const recipients = []
    form.selectedSystemUsers.forEach((user) => {
      recipients.push({ user_id: parseInt(user.value, 10), email: user.email || undefined })
    })
    freeEmails.forEach((email) => {
      recipients.push({ email })
    })
    return recipients
  }

  const save = () => {
    const freeEmails = commitPendingEmail()
    const payload = {
      report_key: reportKey,
      name: form.name,
      frequency: form.frequency,
      frequency_type: form.frequency === 'daily' ? null : form.frequency_type,
      send_time: form.send_time,
      active: form.active,
      recipients: buildRecipients(freeEmails),
    }
    const req = editing
      ? request(`report-email-schedules/${editing.id}`, 'PUT', payload)
      : request('report-email-schedules', 'POST', payload)

    req.then((res) => {
      if (res?.status === 200) {
        dispatch(setNotification({ type: 'success', message: res?.data?.message || 'Saved' }))
        setFormOpen(false)
        loadSchedules()
      } else {
        dispatch(setNotification({ type: 'error', message: res?.data?.message || 'Save failed' }))
      }
    })
  }

  const remove = (id) => {
    request(`report-email-schedules/${id}`, 'DELETE').then((res) => {
      if (res?.status === 200) {
        dispatch(setNotification({ type: 'success', message: 'Schedule deleted' }))
        loadSchedules()
      }
    })
  }

  const [sendingId, setSendingId] = useState(null)
  const [sendingReport, setSendingReport] = useState(false)

  const formatMessage = (message) => {
    if (!message) return 'Send failed'
    if (typeof message === 'string') return message
    if (typeof message === 'object') return Object.values(message).flat().join(' ')
    return 'Send failed'
  }

  const sendNowSchedule = (id) => {
    if (!window.confirm('Send this report email now to the schedule recipients?')) return
    setSendingId(id)
    request(`report-email-schedules/${id}/send-now`, 'POST').then((res) => {
      setSendingId(null)
      if (res?.status === 200) {
        dispatch(setNotification({ type: 'success', message: res?.data?.message || 'Emails queued' }))
        loadSchedules()
      } else {
        dispatch(setNotification({ type: 'error', message: formatMessage(res?.data?.message) }))
      }
    })
  }

  const sendNowReport = () => {
    if (!window.confirm('Send this report now to all active schedule recipients?')) return
    setSendingReport(true)
    request('report-email-schedules/send-now', 'POST', { report_key: reportKey }).then((res) => {
      setSendingReport(false)
      if (res?.status === 200) {
        dispatch(setNotification({ type: 'success', message: res?.data?.message || 'Emails queued' }))
      } else {
        dispatch(setNotification({ type: 'error', message: formatMessage(res?.data?.message) }))
      }
    })
  }

  const selectedFrequency = FREQUENCY_OPTIONS.find((o) => o.value === form.frequency) || FREQUENCY_OPTIONS[1]
  const selectedType = typeOptions.find((o) => o.value === form.frequency_type) || null

  return (
    <>
      <div className='d-inline-flex align-items-center' style={{ gap: '0.5rem' }}>
        <Button size='sm' style={{ background: primary_color, border: 'none' }} onClick={() => setManageOpen(true)}>
          {buttonLabel}
        </Button>
        <Button
          size='sm'
          color='secondary'
          outline
          disabled={sendingReport}
          onClick={sendNowReport}
          title='Send report emails now using active schedules'>
          {sendingReport ? 'Sending…' : 'Send Now'}
        </Button>
      </div>

      <Modal isOpen={manageOpen} toggle={() => setManageOpen(false)} size='lg' centered style={{ boxShadow: 'none' }}>
        <ModalHeader toggle={() => setManageOpen(false)}>Manage email schedules</ModalHeader>
        <ModalBody>
          <div className='d-flex justify-content-end mb-3'>
            <Button size='sm' style={{ background: primary_color, border: 'none' }} onClick={openCreate}>
              Create schedule
            </Button>
          </div>

          {loading ? (
            <div className='text-muted'>Loading schedules…</div>
          ) : schedules.length === 0 ? (
            <div className='text-muted subtitle1'>No schedules yet. Create one to email this report.</div>
          ) : (
            <div className='table-responsive'>
              <table className='table table-sm table-bordered'>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Frequency</th>
                    <th>Time</th>
                    <th>Recipients</th>
                    <th>Active</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.map((row) => (
                    <tr key={row.id}>
                      <td>{row.name}</td>
                      <td>
                        {row.frequency}
                        {row.frequency_type ? ` / ${row.frequency_type}` : ''}
                      </td>
                      <td>{(row.send_time || '').slice(0, 5)}</td>
                      <td>{(row.recipients || []).map((r) => r.email).filter(Boolean).join(', ') || '—'}</td>
                      <td>{row.active ? 'Yes' : 'No'}</td>
                      <td className='text-nowrap'>
                        <Button
                          size='sm'
                          color='link'
                          disabled={sendingId === row.id}
                          onClick={() => sendNowSchedule(row.id)}>
                          {sendingId === row.id ? 'Sending…' : 'Send Now'}
                        </Button>
                        <Button size='sm' color='link' onClick={() => openEdit(row)}>
                          Edit
                        </Button>
                        <Button size='sm' color='link' className='text-danger' onClick={() => remove(row.id)}>
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' onClick={() => setManageOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={formOpen} toggle={() => setFormOpen(false)} size='lg' centered style={{ boxShadow: 'none' }}>
        <ModalHeader toggle={() => setFormOpen(false)}>{editing ? 'Edit schedule' : 'Create schedule'}</ModalHeader>
        <ModalBody>
          <Label>Name</Label>
          <Input className='mb-2' value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />

          <Label>Frequency</Label>
          <div className='mb-2'>
            <Select
              options={FREQUENCY_OPTIONS}
              value={selectedFrequency}
              onChange={(opt) => {
                const frequency = opt?.value || 'weekly'
                setForm({
                  ...form,
                  frequency,
                  frequency_type: frequency === 'weekly' ? 'wed' : frequency === 'monthly' ? '1' : '',
                })
              }}
              styles={selectStyles}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              isSearchable={false}
            />
          </div>

          {typeOptions.length > 0 ? (
            <>
              <Label>{form.frequency === 'weekly' ? 'Day' : 'Day of month'}</Label>
              <div className='mb-2'>
                <Select
                  options={typeOptions}
                  value={selectedType}
                  onChange={(opt) => setForm({ ...form, frequency_type: opt?.value || '' })}
                  styles={selectStyles}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  isSearchable={false}
                />
              </div>
            </>
          ) : null}

          <Label>Send time</Label>
          <Input
            type='time'
            className='mb-2'
            value={form.send_time}
            onChange={(e) => setForm({ ...form, send_time: e.target.value })}
          />

          <Label className='d-block'>
            <input
              type='checkbox'
              className='mr-2'
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            Active
          </Label>

          <Label className='mt-3'>System users</Label>
          <div className='mb-2'>
            <AsyncPaginate
              value={form.selectedSystemUsers}
              loadOptions={loadUserOptions}
              reduceOptions={reduceUserOptions}
              isMulti
              isSearchable
              placeholder='Type name or email to search…'
              onChange={(opts) => setForm({ ...form, selectedSystemUsers: opts || [] })}
              inputValue={userSearchInput}
              onInputChange={(value, { action }) => {
                if (action === 'input-change') {
                  setUserSearchInput(value)
                }
                return value
              }}
              styles={selectStyles}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPlacement='auto'
              maxMenuHeight={280}
              debounceTimeout={300}
              loadOptionsOnMenuOpen
              cacheUniqs={[userSearchInput, formOpen]}
              additional={{ page: 1 }}
            />
          </div>

          <Label>Additional emails</Label>
          <div ref={emailTagsRef}>
            <TagsInput
              value={form.freeEmails}
              onChange={(freeEmails) => setForm({ ...form, freeEmails })}
              onBlur={handleEmailBlur}
              beforeAddValidate={(tag) => isValidEmail(tag)}
              separators={['Enter', 'Tab']}
              name='emails'
              placeHolder='Add email (Enter, Tab, or click away)'
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' onClick={() => setFormOpen(false)}>
            Cancel
          </Button>
          <Button style={{ background: primary_color, border: 'none' }} onClick={save}>
            Save
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default ReportEmailSchedulePanel
